import React from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  IconButton,
  OutlinedInput as MuiOutlinedInput,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import styled from "styled-components";
import useTheme from "../../../../common/hooks/useTheme";
import {
  getBaseInputStyles,
  BaseStyledInputLabel,
  LABEL_TRANSFORM,
} from "../common/BaseInputStyles";

// Read-only, masked value display built on the shared input styling so it
// visually matches TextInputField (56px / 8px radius / floating label).
const StyledSecretInput = styled(MuiOutlinedInput)(({ theme }: { theme: any }) => ({
  ...getBaseInputStyles(theme),
  // Elevated/greyed fill to signal the field is read-only (matches design)
  backgroundColor: theme.colorTokens.N[30],
  "& .MuiOutlinedInput-input": {
    ...getBaseInputStyles(theme)["& .MuiOutlinedInput-input"],
    padding: "24px 12px 10px 12px",
    width: "100%",
    cursor: "default",
  },
}));

type Props = {
  label: string;
  displayValue: string;
  revealed: boolean;
  onToggleReveal: () => void;
};

const MaskedSecretField = ({
  label,
  displayValue,
  revealed,
  onToggleReveal,
}: Props) => {
  const theme = useTheme();

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl
        variant="outlined"
        fullWidth
        sx={{ position: "relative", height: "56px", minHeight: "56px" }}
      >
        <BaseStyledInputLabel
          htmlFor={label}
          theme={theme}
          style={{ transform: LABEL_TRANSFORM.shrunk }}
        >
          {label}
        </BaseStyledInputLabel>

        <StyledSecretInput
          id={label}
          value={displayValue}
          readOnly
          theme={theme}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={onToggleReveal}
                edge="end"
                size="small"
                aria-label={revealed ? "Hide value" : "Reveal value"}
                sx={{
                  color: theme.colors_ds.NEUTRAL_ICON_WEAK,
                  "&:hover": { color: theme.colors_ds.NEUTRAL_ICON_DEFAULT },
                }}
              >
                {revealed ? (
                  <VisibilityOutlinedIcon fontSize="small" />
                ) : (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </Box>
  );
};

export default MaskedSecretField;
